import React, { useState, useEffect, useCallback } from 'react';
import { Spin, Drawer } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';

const GOLD = '#C8922A';
const BD = '#D8D2C8';
const BD2 = '#EAE6DF';
const BD3 = '#F0EDE8';
const BG = '#FAF8F5';
const BG2 = '#F4F1EC';
const BG3 = '#EDEAE3';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#C0392B';
const GRN = '#2E7D4F';
const BLUE = '#2B5CE6';
const PUR = '#6741D9';

const TYPE_CONFIG = {
  watch: { label: '腕表', emoji: '⌚', color: BLUE, bg: 'rgba(43,92,230,.1)', border: 'rgba(43,92,230,.3)', barBg: 'rgba(43,92,230,.2)' },
  jewelry: { label: '珠宝', emoji: '💍', color: PUR, bg: 'rgba(103,65,217,.1)', border: 'rgba(103,65,217,.3)', barBg: 'rgba(103,65,217,.2)' },
  jewel: { label: '珠宝', emoji: '💍', color: PUR, bg: 'rgba(103,65,217,.1)', border: 'rgba(103,65,217,.3)', barBg: 'rgba(103,65,217,.2)' },
  bag: { label: '名包', emoji: '👜', color: GOLD, bg: 'rgba(200,146,42,.1)', border: 'rgba(200,146,42,.25)', barBg: 'rgba(200,146,42,.15)' },
  mixed: { label: '综合', emoji: '📦', color: INK3, bg: 'rgba(26,22,18,.06)', border: BD, barBg: 'rgba(26,22,18,.07)' },
};

const DOW_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

const CAT_ICONS = { watch: '⌚', jewelry: '💍', jewel: '💍', bag: '👜' };

// supported_categories / session_type 可能是数组(JSON 列, mysql2 自动解析)或逗号分隔字符串，统一成数组
function toTypeParts(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string') return v.split(',').filter(Boolean);
  return [];
}

function getTypeConfig(type) {
  if (!type) return TYPE_CONFIG.mixed;
  // Handle compound types like 'watch,jewelry,bag'（兼容数组/字符串）
  const parts = toTypeParts(type);
  if (parts.length === 1) return TYPE_CONFIG[parts[0]] || TYPE_CONFIG.mixed;
  // Multi-category: merge labels + emojis, use neutral (mixed) colors
  const label = parts.map(p => TYPE_CONFIG[p]?.label || p).join('·');
  const emoji = parts.map(p => TYPE_CONFIG[p]?.emoji || '').join('');
  return { ...TYPE_CONFIG.mixed, label, emoji };
}

/**
 * 返回 sessionType 与 houseSupported 的交集类型字符串。
 * 若交集为空则原样返回 sessionType（防止误删）。
 * 例: sessionType='watch,jewelry', houseSupported='watch,jewelry,bag' → 'watch,jewelry'
 */
function intersectTypes(sessionType, houseSupported) {
  const sParts = toTypeParts(sessionType);
  if (!sParts.length) return 'mixed';
  const hParts = toTypeParts(houseSupported);
  if (!hParts.length) return sParts.join(',');
  const common = sParts.filter(t => hParts.includes(t));
  return common.length > 0 ? common.join(',') : sParts.join(',');
}

export default function SessionCalendar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('cal'); // 'cal' or 'tl'
  const [drawerSession, setDrawerSession] = useState(null);
  const [drawerLots, setDrawerLots] = useState([]);
  const today = dayjs();

  // Fetch representative lots when drawer opens
  useEffect(() => {
    if (!drawerSession?.id) { setDrawerLots([]); return; }
    api.get('/lots', { params: { session_id: drawerSession.id, page_size: 3 } })
      .then(({ data }) => setDrawerLots(data.data || []))
      .catch(() => setDrawerLots([]));
  }, [drawerSession?.id]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sessions', { params: { month, year } });
      setSessions(data.data || data || []);
    } catch (err) {
      console.error('Failed to fetch sessions', err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    setYear(today.year());
    setMonth(today.month() + 1);
  };

  // Build calendar grid
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const prevMonthDays = new Date(year, month - 1, 0).getDate();

  // Build sessions indexed by day.
  // For cross-month sessions, anchor to the most relevant date in the viewed month:
  //   1. preview_at if it falls in this month
  //   2. start_at   if it falls in this month
  //   3. day 1 of this month (session was already ongoing at month start)
  const sessionsByDay = {};
  sessions.forEach(s => {
    const candidates = [s.preview_at, s.start_at].filter(Boolean).map(t => dayjs(t));
    const inMonth = candidates.find(t => t.year() === year && t.month() + 1 === month);
    const d = inMonth ? inMonth.date() : 1; // cross-month sessions pin to day 1
    if (d >= 1 && d <= daysInMonth) {
      if (!sessionsByDay[d]) sessionsByDay[d] = [];
      sessionsByDay[d].push(s);
    }
  });

  const totalSessions = sessions.length;
  const mySessions = sessions.filter(s => s.has_mandate || s.mine).length;

  // Build timeline rows by house
  const houseMap = {};
  sessions.forEach(s => {
    const house = s.house_name || s.house || 'Unknown';
    if (!houseMap[house]) houseMap[house] = [];
    houseMap[house].push(s);
  });
  const houses = Object.keys(houseMap);
  const DAY_W = 28;

  const renderDrawerContent = () => {
    if (!drawerSession) return null;
    const s = drawerSession;
    const tc = getTypeConfig(intersectTypes(s.session_type || s.type, s.supported_categories));
    const lots = drawerLots;
    const startDate = s.start_at ? dayjs(s.start_at) : null;
    const previewDate = s.preview_at ? dayjs(s.preview_at) : null;

    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: INK4, letterSpacing: 1, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${BD3}` }}>{t('session.title')}</div>
          {[
            { k: t('session.auctionHouse'), v: s.house_name || s.house },
            { k: t('session.title'), v: s.name },
            { k: t('session.type'), v: `${tc.emoji} ${tc.label}` },
            // ⚠️ 时间值是 DB 北京时间,原来标 JST 是错的(用户看到的其实是北京时间);与全站口径一致去掉 JST 后缀
            { k: t('session.previewTime'), v: previewDate ? previewDate.format('M月D日 HH:mm') : '--' },
            { k: t('session.startTime'), v: startDate ? startDate.format('M月D日 HH:mm') : '--' },
            { k: t('session.endTime'), v: s.end_at ? dayjs(s.end_at).format('M月D日 HH:mm') : (s.end || '--') },
            { k: t('session.lotCount'), v: `${s.lot_count || s.lots || '--'} 件`, color: GOLD },
            { k: t('mandates.title'), v: (s.has_mandate || s.mine) ? `${s.mandate_count || s.mandates || 0} 件` : '无委托', color: (s.has_mandate || s.mine) ? GRN : undefined },
          ].map(row => (
            <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: `1px solid ${BD3}` }}>
              <span style={{ color: INK3 }}>{row.k}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 500, color: row.color || INK2 }}>{row.v}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: INK4, letterSpacing: 1, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${BD3}` }}>{t('browse.title')}</div>
          {lots.length > 0 ? lots.map((l, i) => (
            <div key={l.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${BD3}` }}>
              <span style={{ fontSize: 20 }}>{CAT_ICONS[l.category] || '📦'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{l.title_zh || l.title_ja || '--'}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: INK3 }}>
                  {l.start_price_jpy ? `¥${Number(l.start_price_jpy).toLocaleString()}起` : '--'}
                </div>
              </div>
            </div>
          )) : (
            <div style={{ fontSize: 12, color: INK4, padding: '12px 0' }}>{t('common.noData')}</div>
          )}
        </div>

        <button onClick={() => { setDrawerSession(null); navigate(`/sessions/${s.id}/lots`); }} style={{
          width: '100%', padding: 12, background: INK, color: '#fff', border: 'none', borderRadius: 3,
          fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>{t('session.viewLots')}</button>
        {(s.has_mandate || s.mine) && (
          <button onClick={() => { setDrawerSession(null); navigate('/auction'); }} style={{
            width: '100%', padding: 12, background: GOLD, color: '#fff', border: 'none', borderRadius: 3,
            fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8,
          }}>{t('auction.title')}</button>
        )}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: 'var(--sans,sans-serif)', background: BG, color: INK, fontSize: 14, minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px' }}>
        {/* Month navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 24, fontWeight: 600 }}>{year}年{month}月</div>
            <div style={{ fontSize: 13, color: INK3, marginTop: 2 }}>
              共 <span>{totalSessions}</span> 个场次 · 其中 <span>{mySessions}</span> 个我有委托
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={prevMonth} style={navBtnStyle}>{t('common.back')}</button>
            <button onClick={goToday} style={navBtnStyle}>{t('common.all')}</button>
            <button onClick={nextMonth} style={navBtnStyle}>{t('common.more')}</button>
          </div>
        </div>

        {/* Legend + view toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              { type: 'watch', label: t('browse.watches') },
              { type: 'jewelry', label: t('browse.jewelry') },
              { type: 'bag', label: t('browse.bags') },
              { type: 'mixed', label: t('browse.other') },
            ].map(item => {
              const tc = getTypeConfig(item.type);
              return (
                <div key={item.type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: INK3 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: tc.bg, border: `1px solid ${tc.color}` }} />
                  {item.label}
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: GOLD, fontWeight: 500 }}>★ 我有委托 = 彩色边框</div>
          </div>
          <div style={{ display: 'flex', gap: 0, border: `1px solid ${BD}`, borderRadius: 3, overflow: 'hidden' }}>
            <button onClick={() => setViewMode('cal')} style={{
              padding: '7px 18px', fontSize: 13, cursor: 'pointer', border: 'none',
              background: viewMode === 'cal' ? INK : '#fff', color: viewMode === 'cal' ? '#fff' : INK3,
            }}>{t('session.calendarView')}</button>
            <button onClick={() => setViewMode('tl')} style={{
              padding: '7px 18px', fontSize: 13, cursor: 'pointer', border: 'none',
              background: viewMode === 'tl' ? INK : '#fff', color: viewMode === 'tl' ? '#fff' : INK3,
            }}>{t('session.timelineView')}</button>
          </div>
        </div>

        <Spin spinning={loading}>
          {/* Empty state */}
          {!loading && sessions.length === 0 && (
            <div style={{
              background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4,
              padding: '40px 24px', textAlign: 'center', marginBottom: 16,
              boxShadow: '0 2px 12px rgba(26,22,18,.08)',
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: INK2, marginBottom: 6 }}>
                {year}年{month}月 暂无场次安排
              </div>
              <div style={{ fontSize: 13, color: INK3, marginBottom: 16 }}>
                可切换至上月或下月查看已有场次
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={prevMonth} style={{ padding: '8px 20px', border: `1px solid ${BD}`, borderRadius: 3, background: '#fff', color: INK3, fontSize: 13, cursor: 'pointer' }}>{t('common.back')}</button>
                <button onClick={nextMonth} style={{ padding: '8px 20px', border: `1px solid ${BD}`, borderRadius: 3, background: '#fff', color: INK3, fontSize: 13, cursor: 'pointer' }}>{t('common.more')}</button>
              </div>
            </div>
          )}

          {/* Calendar view */}
          {viewMode === 'cal' && (
            <div style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4, boxShadow: '0 2px 12px rgba(26,22,18,.08)', marginBottom: 20, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {/* 手机修复:场次徽章不换行把 1fr 列撑爆视口,右侧列被硬裁掉(2026-07-08 实测)。
                  外层 overflowX:auto + 内层 minWidth 兜底 → 小屏横向滑动查看,不再裁切。 */}
              <div style={{ minWidth: 640 }}>
              {/* DOW header */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: BG2, borderBottom: `1px solid ${BD2}` }}>
                {DOW_LABELS.map((d, i) => (
                  <div key={d} style={{
                    padding: 10, textAlign: 'center', fontSize: 12, fontWeight: 600,
                    color: i === 0 ? RED : i === 6 ? BLUE : INK3,
                  }}>{d}</div>
                ))}
              </div>
              {/* Calendar cells */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
                {/* Previous month padding */}
                {Array.from({ length: firstDayOfMonth }, (_, i) => (
                  <div key={`prev-${i}`} style={cellStyle(true)}>
                    <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500, color: INK, marginBottom: 4, opacity: 0.35 }}>
                      {prevMonthDays - firstDayOfMonth + 1 + i}
                    </div>
                  </div>
                ))}
                {/* Current month days */}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dow = (firstDayOfMonth + i) % 7;
                  const isToday = today.year() === year && today.month() + 1 === month && today.date() === day;
                  const daySessions = sessionsByDay[day] || [];
                  return (
                    <div key={day} style={{
                      ...cellStyle(false),
                      background: isToday ? 'rgba(200,146,42,.04)' : '#fff',
                    }}>
                      <div style={{
                        fontFamily: 'monospace', fontSize: 13, fontWeight: 500, marginBottom: 4,
                        color: dow === 0 ? RED : dow === 6 ? BLUE : INK,
                        ...(isToday ? {
                          background: INK, color: '#fff', borderRadius: '50%',
                          width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        } : { width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
                      }}>{day}</div>
                      {daySessions.map((s, si) => {
                        const tc = getTypeConfig(intersectTypes(s.session_type || s.type, s.supported_categories));
                        const isMine = s.has_mandate || s.mine;
                        return (
                          <span key={si}
                            onClick={() => setDrawerSession(s)}
                            style={{
                              fontSize: 10, padding: '2px 6px', borderRadius: 2, marginBottom: 2,
                              cursor: 'pointer', display: 'block', lineHeight: 1.5,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              background: tc.bg, color: tc.color,
                              ...(isMine ? { outline: `1.5px solid ${tc.color}`, outlineOffset: -1 } : {}),
                              transition: 'opacity .13s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                          >
                            {s.house_name || s.house} · {tc.emoji}{tc.label}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
                {/* Next month padding */}
                {(() => {
                  const remaining = 7 - ((firstDayOfMonth + daysInMonth) % 7);
                  if (remaining >= 7) return null;
                  return Array.from({ length: remaining }, (_, i) => (
                    <div key={`next-${i}`} style={cellStyle(true)}>
                      <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500, color: INK, marginBottom: 4, opacity: 0.35 }}>
                        {i + 1}
                      </div>
                    </div>
                  ));
                })()}
              </div>
              </div>{/* /minWidth 兜底容器 */}
            </div>
          )}

          {/* Timeline view */}
          {viewMode === 'tl' && (
            <div style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(26,22,18,.08)' }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{month}月场次时间轴</span>
                <span style={{ fontSize: 12, color: INK3 }}>横轴 = 日期（1~{daysInMonth}日）</span>
              </div>
              <div style={{ overflowX: 'auto', padding: '16px 18px' }}>
                {/* Date ruler */}
                <div style={{ display: 'flex', minWidth: daysInMonth * DAY_W + 100, marginLeft: 100, paddingLeft: 0, marginBottom: 8 }}>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const isToday = today.year() === year && today.month() + 1 === month && today.date() === day;
                    return (
                      <div key={day} style={{
                        flex: 1, minWidth: DAY_W, fontFamily: 'monospace', fontSize: 10,
                        color: isToday ? RED : INK4, fontWeight: isToday ? 600 : 400,
                        textAlign: 'center', borderLeft: '1px dashed rgba(240,237,232,.8)', paddingTop: 2,
                      }}>{day}</div>
                    );
                  })}
                </div>
                {/* House rows */}
                {houses.map(house => (
                  <div key={house} style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 10, minWidth: daysInMonth * DAY_W + 100 }}>
                    <div style={{ width: 100, flexShrink: 0, fontSize: 12, fontWeight: 500, color: INK2, textAlign: 'right', paddingRight: 12 }}>
                      {house}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: 36, background: BG3, borderRadius: 3, minWidth: daysInMonth * DAY_W }}>
                      {houseMap[house].map((s, si) => {
                        const _sCandidates = [s.preview_at, s.start_at].filter(Boolean).map(t => dayjs(t));
                        const _sInMonth = _sCandidates.find(t => t.year() === year && t.month() + 1 === month);
                        const sDay = _sInMonth ? _sInMonth.date() : 1;
                        const tc = getTypeConfig(intersectTypes(s.session_type || s.type, s.supported_categories));
                        const left = (sDay - 1) * DAY_W;
                        const isMine = s.has_mandate || s.mine;
                        const label = s.name || '';
                        return (
                          <div key={si}
                            onClick={() => setDrawerSession(s)}
                            title={`${s.name} · ${s.start_at ? dayjs(s.start_at).format('HH:mm') : ''}~${s.end_at ? dayjs(s.end_at).format('HH:mm') : ''}`}
                            style={{
                              position: 'absolute', top: 4, height: 28, borderRadius: 3,
                              display: 'flex', alignItems: 'center', padding: '0 8px',
                              fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
                              left, width: DAY_W - 2,
                              background: tc.barBg, color: tc.color, border: `1px solid ${tc.border}`,
                              ...(isMine ? { outline: `1.5px solid ${tc.color}`, outlineOffset: -1 } : {}),
                              transition: 'opacity .13s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                          >
                            {tc.emoji} {tc.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Spin>
      </div>

      {/* Session detail drawer */}
      <Drawer
        title={drawerSession ? `${drawerSession.house_name || drawerSession.house} · ${drawerSession.name}` : '场次详情'}
        placement="right"
        width={380}
        onClose={() => setDrawerSession(null)}
        open={!!drawerSession}
        styles={{ header: { fontFamily: 'var(--serif,serif)', fontSize: 17, fontWeight: 600 } }}
      >
        {renderDrawerContent()}
      </Drawer>
    </div>
  );
}

const navBtnStyle = {
  padding: '7px 16px', border: `1px solid #D8D2C8`, borderRadius: 3,
  fontSize: 13, cursor: 'pointer', background: '#fff', color: '#7A7268',
};

function cellStyle(otherMonth) {
  return {
    minHeight: 100, borderRight: `1px solid #F0EDE8`, borderBottom: `1px solid #F0EDE8`,
    padding: 6, position: 'relative',
    ...(otherMonth ? { opacity: 0.35 } : {}),
  };
}
